import type {
	DeleteResponse,
	EnvironmentRepository,
	FindAllResponse,
	GetResponse,
	HttpService,
	PatchResponse,
	PostResponse,
	PutResponse,
	UseCaseCriteria
} from '@contexts/shared/domain';
import { criteriaParsed } from '@contexts/shared/infrastructure';
import type {
	UpdateApiKeyAccessInput,
	UpdateLastAlertsCheckInput,
	UpdateUserInput,
	User,
	UserCore,
	UserRepository
} from '@contexts/users/domain';
import { userMapper } from '@contexts/users/infrastructure/rest-api/mappers/user.mapper';

export function restApiUserRepositoryBuilder(
	environment: EnvironmentRepository,
	httpService: HttpService
): UserRepository {
	const mapper = userMapper();

	return {
		async create(user: UserCore): Promise<User> {
			const response = await httpService.post<PostResponse<User>>(
				`${environment.apiBaseUrl}/users`,
				{ ...user, language: user.language }
			);

			return mapper.fromDTO(response.data.item);
		},
		async update(user: UpdateUserInput): Promise<User> {
			const response = await httpService.put<PutResponse<User>>(
				`${environment.apiBaseUrl}/users/${user.id}`,
				user
			);

			return mapper.fromDTO(response.data.item);
		},
		async updateApiKeyAccess(input: UpdateApiKeyAccessInput): Promise<User> {
			const response = await httpService.patch<PatchResponse<User>>(
				`${environment.apiBaseUrl}/users/${input.id}/api-key`,
				{ hasApiKey: input.hasApiKey }
			);

			return mapper.fromDTO(response.data.item);
		},
		async updateLastAlertsCheck(input: UpdateLastAlertsCheckInput): Promise<User> {
			const response = await httpService.patch<PatchResponse<User>>(
				`${environment.apiBaseUrl}/users/${input.id}/last-alerts-check`,
				{ lastAlertsCheck: input.lastAlertsCheck }
			);

			return response.data.item;
		},
		async delete(id: string): Promise<string> {
			const response = await httpService.delete<DeleteResponse>(
				`${environment.apiBaseUrl}/users/${id}`
			);

			return response.data.id;
		},
		async findAll(criteria?: UseCaseCriteria): Promise<FindAllResponse<User>> {
			const response = await httpService.get<FindAllResponse<User>>(
				`${environment.apiBaseUrl}/users`,
				criteriaParsed(criteria)
			);

			return {
				...response.data,
				items: response.data.items.map(mapper.fromDTO)
			};
		},
		async getLoggedUser(): Promise<User> {
			const response = await httpService.get<GetResponse<User>>(
				`${environment.apiBaseUrl}/auth/me`
			);

			return mapper.fromDTO(response.data.item);
		},
		async resendActivationCode(id: string): Promise<void> {
			await httpService.post<PostResponse<string>>(
				`${environment.apiBaseUrl}/users/${id}/re-activate`,
				undefined
			);
		}
	};
}
